import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Trash2, Plus } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import NoDataFound from '../common/NoDataFound';
import { confirmDelete } from '../common/confirm';
import { extractApiError } from '../../utils/apiError';
import {
  Finding,
  Report,
  ReportTemplate,
  FindingReportMembership,
  reportsService,
  reportTemplatesService,
  findingService,
} from '../../services/auditService';

/**
 * PushToReportModal — finding-level report placement picker.
 *
 * Replaces the old single-flag toggle. Lets the auditor choose WHICH report and
 * WHICH section a finding goes into (and remove it again). Adding is additive —
 * a finding can sit in several reports / sections. Backed by:
 *   POST   /adt/reports/{id}/memos              (additive, non-destructive)
 *   DELETE /adt/reports/{id}/memos/{finding_id}
 *   GET    /adt/findings/{id}/report-memberships
 *
 * The backend keeps the legacy pushed_to_report_at flag in sync, so onChanged()
 * lets the parent FindingDetailModal refresh its "In Report" line.
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  finding: Finding | null;
  /** Called after any add/remove so the parent can refetch the finding. */
  onChanged?: () => void;
}

const PushToReportModal: React.FC<Props> = ({ isOpen, onClose, finding, onChanged }) => {
  const [memberships, setMemberships] = useState<FindingReportMembership[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add-form state.
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [reportId, setReportId] = useState<number | ''>('');
  // A finding is frequently relevant under several paragraphs of the same
  // report, so this is a SET. The DB already allows it — uniqueness is
  // (report_id, section_key, finding_id) — the single-select was the only
  // thing forcing one round-trip per paragraph.
  const [sectionKeys, setSectionKeys] = useState<Set<string>>(new Set());
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [newTitle, setNewTitle] = useState<string>('');

  const findingId = finding?.id ?? null;

  const load = useCallback(async () => {
    if (!findingId) return;
    setLoading(true);
    setError(null);
    try {
      const [mems, repList, tmplList] = await Promise.all([
        findingService.reportMemberships(findingId),
        reportsService.list({ page: 1, limit: 200 }),
        reportTemplatesService.list({ page: 1, limit: 200 }),
      ]);
      setMemberships(mems);
      setReports(repList.items);
      setTemplates(tmplList.items);
      // Default to "new" when no reports exist yet.
      setMode(repList.items.length ? 'existing' : 'new');
    } catch (e: any) {
      setError(extractApiError(e, 'Failed to load report options.'));
    } finally {
      setLoading(false);
    }
  }, [findingId]);

  useEffect(() => {
    if (isOpen) {
      // reset add-form each open
      setReportId('');
      setSectionKeys(new Set());
      setTemplateId('');
      setNewTitle('');
      load();
    }
  }, [isOpen, load]);

  // Section options derive from the chosen report's template (existing) or the
  // chosen template (new). Reset section when the source changes.
  const selectedReport = reports.find((r) => r.id === reportId);
  const selectedTemplate = templates.find((t) => t.id === templateId);
  const sectionOptions =
    mode === 'existing'
      ? selectedReport?.template_sections ?? []
      : selectedTemplate?.sections ?? [];

  const refreshAfterMutation = useCallback(async () => {
    if (!findingId) return;
    const mems = await findingService.reportMemberships(findingId);
    setMemberships(mems);
    onChanged?.();
  }, [findingId, onChanged]);

  const handleAdd = useCallback(async () => {
    if (!findingId) return;
    if (sectionKeys.size === 0) {
      setError('Pick at least one paragraph for the finding.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let targetReportId: number;
      if (mode === 'new') {
        if (!templateId) { setError('Pick a report template.'); setBusy(false); return; }
        if (!newTitle.trim()) { setError('Give the new report a title.'); setBusy(false); return; }
        const created = await reportsService.create({
          report_template_id: Number(templateId),
          title: newTitle.trim(),
          program_instance_id: (finding as any)?.program_instance_id ?? undefined,
        });
        targetReportId = created.id;
      } else {
        if (!reportId) { setError('Pick a report.'); setBusy(false); return; }
        targetReportId = Number(reportId);
      }
      // One call per checked paragraph. addMemo is additive, so placements in
      // OTHER reports are untouched and the finding accumulates across reports.
      for (const key of Array.from(sectionKeys)) {
        await reportsService.addMemo(targetReportId, {
          finding_id: findingId,
          section_key: key,
        });
      }
      // Reset add-form and reload everything (new report must appear in list).
      setSectionKeys(new Set());
      setNewTitle('');
      setTemplateId('');
      const repList = await reportsService.list({ page: 1, limit: 200 });
      setReports(repList.items);
      if (mode === 'new') { setMode('existing'); setReportId(targetReportId); }
      await refreshAfterMutation();
    } catch (e: any) {
      setError(extractApiError(e, 'Failed to add finding to the report.'));
    } finally {
      setBusy(false);
    }
  }, [findingId, mode, reportId, templateId, newTitle, sectionKeys, finding, refreshAfterMutation]);

  const handleRemove = useCallback(async (m: FindingReportMembership) => {
    if (!findingId) return;
    const confirmed = await confirmDelete({
      title: `Remove from "${m.section_title}"?`,
      text: `The finding stays linked to any other paragraph of "${m.report_title}".`,
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      // Section-scoped: a finding may sit under several paragraphs, and the
      // row being removed is one placement. removeMemo() would clear EVERY
      // placement in this report, which is not what the row's X means.
      await reportsService.removeMemoFromSection(m.report_id, findingId, m.section_key);
      await refreshAfterMutation();
    } catch (e: any) {
      setError(extractApiError(e, 'Failed to remove finding from the report.'));
    } finally {
      setBusy(false);
    }
  }, [findingId, refreshAfterMutation]);

  if (!finding) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnEscape closeOnOverlayClick data-testid="MOD_push_to_report_root">
      <Modal.Header>
        <span className="modal-header-icon-row">
          <FileText size={16} />
          Push Finding to Report
        </span>
      </Modal.Header>
      <Modal.Body>
        <div className="observation-context-card">
          <div className="observation-context-title">
            {(finding.findings_text || 'Finding').slice(0, 120)}
          </div>
          <div className="observation-context-row">
            {(finding as any).program_name ?? '—'}
            {(finding as any).procedure_name ? ` · ${(finding as any).procedure_name}` : ''}
          </div>
        </div>

        {error && (
          <div className="inline-error inline-error--inline" role="alert">
            {error}
          </div>
        )}

        {/* Current placements */}
        <div className="assign-form-block">
          <label className="form-control-label">In reports</label>
          {loading ? (
            <p className="modal-empty-text">Loading…</p>
          ) : memberships.length === 0 ? (
            <NoDataFound message="Not in any report yet." />
          ) : (
            <ul className="ptr-membership-list" data-testid="LIST_push_to_report_memberships">
              {memberships.map((m) => (
                <li key={`${m.report_id}:${m.section_key}`} className="ptr-membership-row">
                  <span className="ptr-membership-text">
                    <strong>{m.report_title}</strong>
                    <span className="ptr-membership-section"> — {m.section_title}</span>
                    {m.report_status === 'generated' && (
                      <span className="ptr-membership-badge"> generated</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="ptr-membership-remove"
                    title="Remove from this report"
                    disabled={busy}
                    onClick={() => handleRemove(m)}
                    data-testid={`BTN_push_to_report_remove_${m.report_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add to a report */}
        <div className="assign-form-block ptr-add-block">
          <label className="form-control-label">Add to a report</label>

          <div className="ptr-mode-toggle">
            <button
              type="button"
              className={`ptr-mode-btn${mode === 'existing' ? ' ptr-mode-btn--active' : ''}`}
              onClick={() => { setMode('existing'); setSectionKeys(new Set()); }}
              disabled={busy}
              data-testid="BTN_push_to_report_mode_existing"
            >
              Existing report
            </button>
            <button
              type="button"
              className={`ptr-mode-btn${mode === 'new' ? ' ptr-mode-btn--active' : ''}`}
              onClick={() => { setMode('new'); setSectionKeys(new Set()); }}
              disabled={busy}
              data-testid="BTN_push_to_report_mode_new"
            >
              New report
            </button>
          </div>

          {mode === 'existing' ? (
            <FormControl.Select
              label="Report"
              value={reportId === '' ? '' : String(reportId)}
              disabled={busy || reports.length === 0}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setReportId(e.target.value ? Number(e.target.value) : '');
                setSectionKeys(new Set());
              }}
              data-testid="SEL_push_to_report_report"
            >
              <option value="">{reports.length ? 'Select report…' : 'No reports yet'}</option>
              {reports.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title}{r.template_name ? ` (${r.template_name})` : ''}
                </option>
              ))}
            </FormControl.Select>
          ) : (
            <>
              <FormControl.Select
                label="Report template"
                value={templateId === '' ? '' : String(templateId)}
                disabled={busy || templates.length === 0}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setTemplateId(e.target.value ? Number(e.target.value) : '');
                  setSectionKeys(new Set());
                }}
                data-testid="SEL_push_to_report_template"
              >
                <option value="">{templates.length ? 'Select template…' : 'No templates defined'}</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </FormControl.Select>
              <FormControl.Input
                label="New report title"
                value={newTitle}
                disabled={busy}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTitle(e.target.value)}
                placeholder="e.g. Q2 Internal Audit Report"
                data-testid="INP_push_to_report_title"
              />
            </>
          )}

          {/* Multi-select: one finding, many paragraphs, in one action.
              Paragraphs it already sits in are shown as such rather than
              offered again, so the auditor sees the full picture. */}
          <div className="ptr-paras">
            <span className="ptr-paras__label">
              Paragraphs
              {sectionKeys.size > 0 && (
                <span className="ptr-paras__count">{sectionKeys.size} selected</span>
              )}
            </span>
            {sectionOptions.length === 0 ? (
              <p className="ptr-paras__hint">Pick a report or template first.</p>
            ) : (
              <div className="ptr-paras__list">
                {sectionOptions.map((sec) => {
                  const already = mode === 'existing' && reportId !== ''
                    && memberships.some(
                      (m) => m.report_id === Number(reportId) && m.section_key === sec.key,
                    );
                  const checked = sectionKeys.has(sec.key);
                  return (
                    <label
                      key={sec.key}
                      className={`ptr-para${already ? ' ptr-para--linked' : ''}`}
                      title={already ? 'Already linked to this paragraph' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={already || checked}
                        disabled={busy || already}
                        onChange={() => setSectionKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(sec.key)) next.delete(sec.key);
                          else next.add(sec.key);
                          return next;
                        })}
                        data-testid={`CHK_push_to_report_section_${sec.key}`}
                      />
                      <span>{sec.title}</span>
                      {already && <span className="ptr-para__tag">linked</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary-outline" onClick={onClose} disabled={busy} data-testid="BTN_push_to_report_close">
          Done
        </Button>
        <Button
          variant="fill"
          onClick={handleAdd}
          disabled={busy || loading || sectionKeys.size === 0}
          data-testid="BTN_push_to_report_add"
        >
          <Plus size={14} />{' '}
          {busy
            ? 'Adding…'
            : sectionKeys.size > 1
              ? `Add to ${sectionKeys.size} paragraphs`
              : 'Add to report'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PushToReportModal;
